import React, { createContext, useContext, useState } from 'react'

import { Buffer } from 'buffer'

import { get } from '../deprecated/features/base'

type FileRecord = Record<string, string>
type RequestRecord = Record<string, { pending: boolean; error: string | null }>

interface DownloadedFilesContextType {
	files: FileRecord
	requests: RequestRecord
	getFile: (fileId: string) => Promise<void>
}

const DownloadedFilesContext = createContext<
	DownloadedFilesContextType | undefined
>(undefined)

export const DownloadedFilesProvider: React.FC<{
	children: React.ReactNode
}> = ({ children }) => {
	const [files, setFiles] = useState<FileRecord>({})
	const [requests, setRequests] = useState<RequestRecord>({})

	const getFile = async (fileId: string) => {
		// If file already exists in state or a request is pending, don't make another request
		if (files[fileId] !== undefined || requests[fileId]?.pending) {
			return
		}

		// Set request as pending
		setRequests(prev => ({
			...prev,
			[fileId]: { pending: true, error: null },
		}))

		try {
			const res = await get(`/api/v2/files/${fileId}`)
			if (res.status === 404) {
				throw new Error('File does not exist')
			}

			const imageBuffer = await res.arrayBuffer()
			const imageStr = Buffer.from(imageBuffer).toString('base64')

			// Update the files state
			setFiles(prev => ({
				...prev,
				[fileId]: imageStr,
			}))

			// Update request status
			setRequests(prev => ({
				...prev,
				[fileId]: { pending: false, error: null },
			}))
		} catch (error) {
			// Handle error
			setRequests(prev => ({
				...prev,
				[fileId]: {
					pending: false,
					error:
						error instanceof Error
							? error.message
							: 'Unexpected error',
				},
			}))
		}
	}

	return (
		<DownloadedFilesContext.Provider
			value={{
				files,
				requests,
				getFile,
			}}
		>
			{children}
		</DownloadedFilesContext.Provider>
	)
}

export const useDownloadedFiles = (): DownloadedFilesContextType => {
	const context = useContext(DownloadedFilesContext)
	if (context === undefined) {
		throw new Error(
			'useDownloadedFiles must be used within a DownloadedFilesProvider'
		)
	}
	return context
}
